/**
 * ShiftCrewAssignment - Component for managing crew assignments on a shift
 * Core Staffing Model - Task Definition + Crew Chief flag
 * EF-CREW-CONFLICT-PREVENTION-V1: Integrated conflict detection and override
 */
import { useState, useEffect, useCallback } from "react";
import {
  getShiftCrew,
  getEligibleShiftCrew,
  addShiftCrew,
  updateShiftCrew,
  removeShiftCrew,
  checkShiftConflicts,
  extractErrorMessage,
} from "../../lib/api";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  X,
  Loader2,
  AlertTriangle,
  Clock,
} from "lucide-react";
import { cn } from "../../lib/utils";

// Predefined roles as fallback
const FALLBACK_ROLES = ["Crew", "Crew Chief", "Supervisor"];

/**
 * Eligible staff option in dropdown
 */
function EligibleStaffOption({ staff }) {
  return (
    <div className="flex items-center justify-between w-full">
      <span className="truncate">{staff.first_name} {staff.last_name}</span>
      {!staff.is_eligible && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 ml-2 shrink-0" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{staff.ineligible_reason || "Not eligible"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/**
 * Assigned crew member row - compact design
 */
function AssignedCrewRow({ assignment, onRemove, onToggleCrewChief, removing, showCrewChiefToggle = false }) {
  // Check if Crew Chief (check for both old "Team Leader" and new "Crew Chief" for backwards compatibility)
  const roleText = assignment.role || "Crew";
  const isCrewChief = assignment.is_crew_chief || 
    roleText.toLowerCase().includes("(crew chief)") ||
    roleText.toLowerCase().includes("(team leader)") ||
    roleText.toLowerCase() === "crew chief" ||
    roleText.toLowerCase() === "team leader" ||
    roleText.toLowerCase().includes("supervisor");
  
  // Get display role (without Crew Chief suffix for cleaner display)
  const displayRole = roleText
    .replace(/\s*\(Crew Chief\)/gi, "")
    .replace(/\s*\(Team Leader\)/gi, "")
    .replace(/^Crew Chief$/i, "Crew")
    .replace(/^Team Leader$/i, "Crew")
    .trim() || "Crew";

  return (
    <div
      className="flex items-center justify-between py-1.5 px-2 rounded bg-surface-elevated/30 border border-border/50 group"
      data-testid={`shift-crew-${assignment.staff_id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {/* Crew Chief indicator - only show when threshold met */}
        {showCrewChiefToggle && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onToggleCrewChief(assignment.staff_id, !isCrewChief)}
                  className={cn(
                    "shrink-0 w-5 h-5 flex items-center justify-center rounded text-sm transition-colors",
                    isCrewChief 
                      ? "bg-amber-500/20" 
                      : "bg-transparent opacity-30 hover:opacity-100 hover:bg-surface-hover"
                  )}
                >
                  ⛑️
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isCrewChief ? "Crew Chief - Click to remove" : "Click to make Crew Chief"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        {/* Crew Chief badge if designated (shown even when toggle hidden) */}
        {!showCrewChiefToggle && isCrewChief && (
          <span className="text-sm">⛑️</span>
        )}
        
        {/* Name */}
        <span className="text-sm font-medium truncate">{assignment.staff_name}</span>
        
        {/* Task/Role badge */}
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 shrink-0">
          {displayRole}
        </Badge>
        
        {/* Crew Chief indicator badge (when toggle not shown but is CC) */}
        {isCrewChief && !showCrewChiefToggle && (
          <Badge className="text-[10px] px-1 py-0 h-5 bg-amber-500/20 text-amber-600 border-amber-500/30">
            CC
          </Badge>
        )}
      </div>
      
      {/* Remove button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
        onClick={() => onRemove(assignment.staff_id)}
        disabled={removing}
        data-testid={`remove-shift-crew-${assignment.staff_id}`}
      >
        {removing ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <X className="h-3 w-3" />
        )}
      </Button>
    </div>
  );
}

/**
 * Main ShiftCrewAssignment component
 */
export function ShiftCrewAssignment({ shiftId, staffRequired = 0, readOnly = false }) {
  const [assignedCrew, setAssignedCrew] = useState([]);
  const [eligibleCrew, setEligibleCrew] = useState([]);
  const [taskDefinitions, setTaskDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  
  // Form state
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [selectedTask, setSelectedTask] = useState("");
  const [isCrewChief, setIsCrewChief] = useState(false);
  
  // Crew Chief thresholds from rule variables
  const [crewChiefThreshold1, setCrewChiefThreshold1] = useState(4);
  const [crewChiefThreshold2, setCrewChiefThreshold2] = useState(12);
  
  // EF-CREW-CONFLICT-PREVENTION-V1: Conflict detection state
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [pendingConflicts, setPendingConflicts] = useState([]);
  const [conflictOverrideReason, setConflictOverrideReason] = useState("");
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    if (!shiftId) return;

    try {
      setLoading(true);
      const [crewRes, eligibleRes, tasksRes, varsRes] = await Promise.all([
        getShiftCrew(shiftId),
        getEligibleShiftCrew(shiftId),
        api.get("/task-definitions?is_active=true"),
        api.get("/rule-variables"),
      ]);
      
      setAssignedCrew(crewRes.data || []);
      setEligibleCrew(eligibleRes.data || []);
      setTaskDefinitions(tasksRes.data || []);
      
      // Set Crew Chief thresholds (check for both old and new variable names for backwards compatibility)
      const vars = varsRes.data || [];
      const t1 = vars.find(v => v.key === "crew_chief_threshold_1") || vars.find(v => v.key === "team_leader_threshold_1");
      const t2 = vars.find(v => v.key === "crew_chief_threshold_2") || vars.find(v => v.key === "team_leader_threshold_2");
      if (t1) setCrewChiefThreshold1(t1.value);
      if (t2) setCrewChiefThreshold2(t2.value);
    } catch (err) {
      console.error("Failed to load shift crew:", extractErrorMessage(err));
      toast.error("Failed to load crew assignments");
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculations
  const assignedCount = assignedCrew.length;
  const needsCrewChief = assignedCount >= crewChiefThreshold1;
  const needsTwoCrewChiefs = assignedCount >= crewChiefThreshold2;
  const crewChiefCount = assignedCrew.filter(c => 
    c.is_crew_chief ||
    c.role?.toLowerCase().includes("lead") || 
    c.role?.toLowerCase().includes("supervisor")
  ).length;
  const requiredCrewChiefs = needsTwoCrewChiefs ? 2 : needsCrewChief ? 1 : 0;
  const showCrewChiefWarning = requiredCrewChiefs > 0 && crewChiefCount < requiredCrewChiefs;

  // Get available crew (not yet assigned)
  const availableCrew = eligibleCrew.filter((s) => s.is_eligible);
  const unavailableCrew = eligibleCrew.filter((s) => !s.is_eligible);

  // Assign crew member - with conflict checking
  const handleAssign = async (overrideConflicts = false, overrideReason = null) => {
    if (!selectedStaffId) {
      toast.error("Please select a crew member");
      return;
    }

    // Build role string - Task name + Crew Chief suffix if applicable
    let role = selectedTask || "Crew";
    if (isCrewChief && !role.toLowerCase().includes("lead")) {
      role = `${role} (Crew Chief)`;
    }

    try {
      setAssigning(true);
      
      // EF-CREW-CONFLICT-PREVENTION-V1: Try to add with optional override
      await addShiftCrew(shiftId, selectedStaffId, role, null, overrideConflicts, overrideReason);
      toast.success("Crew member assigned");
      
      // Reset form and close dialog
      setSelectedStaffId("");
      setSelectedTask("");
      setIsCrewChief(false);
      setConflictDialogOpen(false);
      setPendingConflicts([]);
      setConflictOverrideReason("");
      setPendingAssignment(null);
      await loadData();
    } catch (err) {
      const errorData = err.response?.data?.detail;
      
      // EF-CREW-CONFLICT-PREVENTION-V1: Handle conflict error
      if (typeof errorData === 'object' && errorData?.error_type === 'SHIFT_CONFLICT') {
        // Show conflict dialog
        setPendingConflicts(errorData.conflicts || []);
        setPendingAssignment({ staffId: selectedStaffId, role });
        setConflictDialogOpen(true);
      } else {
        const msg = typeof errorData === 'string' ? errorData : extractErrorMessage(err);
        toast.error(msg || "Failed to assign crew member");
      }
    } finally {
      setAssigning(false);
    }
  };
  
  // EF-CREW-CONFLICT-PREVENTION-V1: Handle conflict override
  const handleConflictOverride = async () => {
    if (!conflictOverrideReason.trim()) {
      toast.error("Please provide a reason for overriding the conflict");
      return;
    }
    
    if (pendingAssignment) {
      await handleAssign(true, conflictOverrideReason.trim());
    }
  };

  // Toggle Crew Chief status
  const handleToggleCrewChief = async (staffId, makeCrewChief) => {
    const assignment = assignedCrew.find(c => c.staff_id === staffId);
    if (!assignment) return;

    let newRole = assignment.role || "Crew";
    
    // First, clean up any existing Crew Chief/Team Leader markers
    newRole = newRole.replace(/\s*\(Crew Chief\)/gi, "").replace(/\s*\(Team Leader\)/gi, "").trim();
    if (newRole.toLowerCase() === "crew chief" || newRole.toLowerCase() === "team leader") {
      newRole = "Crew";
    }
    
    if (makeCrewChief) {
      // Add Crew Chief marker
      newRole = `${newRole} (Crew Chief)`;
    }

    try {
      await updateShiftCrew(shiftId, staffId, newRole);
      toast.success(makeCrewChief ? "Set as Crew Chief" : "Removed Crew Chief status");
      await loadData();
    } catch (err) {
      toast.error("Failed to update role");
    }
  };

  // Remove crew member
  const handleRemove = async (staffId) => {
    try {
      setRemovingId(staffId);
      await removeShiftCrew(shiftId, staffId);
      toast.success("Crew member removed");
      await loadData();
    } catch (err) {
      toast.error("Failed to remove crew member");
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with count */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <span className="font-medium text-sm">Assigned Crew</span>
          <Badge 
            variant="secondary" 
            className={cn(
              "text-xs",
              staffRequired > 0 && assignedCount < staffRequired && "bg-yellow-500/20 text-yellow-600",
              staffRequired > 0 && assignedCount >= staffRequired && "bg-green-500/20 text-green-600"
            )}
          >
            {staffRequired > 0 ? `${assignedCount} of ${staffRequired}` : assignedCount}
          </Badge>
        </div>
        
        {/* Crew Chief count */}
        {requiredCrewChiefs > 0 && (
          <Badge 
            variant="outline" 
            className={cn(
              "text-xs gap-1",
              crewChiefCount < requiredCrewChiefs 
                ? "border-amber-500/50 text-amber-600" 
                : "border-green-500/50 text-green-600"
            )}
          >
            <span>⛑️</span>
            {crewChiefCount}/{requiredCrewChiefs} CC
          </Badge>
        )}
      </div>

      {/* Crew Chief Warning */}
      {showCrewChiefWarning && (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-600 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            {requiredCrewChiefs === 1 
              ? "A Crew Chief is required" 
              : `${requiredCrewChiefs} Crew Chiefs required`}
          </span>
        </div>
      )}

      {/* Add Crew Form */}
      {!readOnly && (
        <div className="p-2.5 rounded border border-border/50 bg-surface-elevated/10 space-y-2">
          {/* Row 1: Crew + Task */}
          <div className="flex gap-2">
            <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
              <SelectTrigger className="flex-1 h-8 text-sm" data-testid="shift-crew-staff-selector">
                <SelectValue placeholder="Select crew..." />
              </SelectTrigger>
              <SelectContent>
                {availableCrew.length === 0 && unavailableCrew.length === 0 ? (
                  <div className="p-2 text-xs text-muted-foreground text-center">
                    No crew available
                  </div>
                ) : (
                  <>
                    {availableCrew.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id} className="text-sm">
                        <EligibleStaffOption staff={staff} />
                      </SelectItem>
                    ))}
                    {unavailableCrew.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-[10px] text-muted-foreground border-t mt-1">
                          Unavailable
                        </div>
                        {unavailableCrew.map((staff) => (
                          <SelectItem key={staff.id} value={staff.id} disabled className="text-sm">
                            <EligibleStaffOption staff={staff} />
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </>
                )}
              </SelectContent>
            </Select>

            <Select value={selectedTask} onValueChange={setSelectedTask}>
              <SelectTrigger className="w-32 h-8 text-sm" data-testid="shift-crew-task-selector">
                <SelectValue placeholder="Task..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Crew" className="text-sm">Crew (General)</SelectItem>
                {taskDefinitions.map((task) => (
                  <SelectItem key={task.id} value={task.name} className="text-sm">
                    {task.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: Crew Chief checkbox (only when threshold met) + Assign button */}
          <div className="flex items-center justify-between">
            {/* Crew Chief checkbox - only show when threshold will be met after adding */}
            {needsCrewChief || (assignedCount + 1) >= crewChiefThreshold1 ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="crew-chief"
                  checked={isCrewChief}
                  onCheckedChange={setIsCrewChief}
                  data-testid="crew-chief-checkbox"
                />
                <Label htmlFor="crew-chief" className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1">
                  <span>⛑️</span> Crew Chief
                </Label>
              </div>
            ) : (
              <div /> // Empty spacer
            )}

            <Button
              onClick={() => handleAssign()}
              disabled={!selectedStaffId || assigning}
              size="sm"
              className="h-7 text-xs"
              data-testid="shift-crew-assign-btn"
            >
              {assigning ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <UserPlus className="h-3 w-3 mr-1" />
              )}
              Assign
            </Button>
          </div>
        </div>
      )}

      {/* Assigned Crew List - scrollable */}
      <div className="max-h-40 overflow-y-auto space-y-1">
        {assignedCrew.length === 0 ? (
          <div className="py-4 text-center border border-dashed border-border/50 rounded">
            <Users className="h-5 w-5 mx-auto text-muted-foreground/40 mb-1" />
            <p className="text-xs text-muted-foreground">No crew assigned</p>
          </div>
        ) : (
          assignedCrew.map((assignment) => (
            <AssignedCrewRow
              key={assignment.id}
              assignment={assignment}
              onRemove={handleRemove}
              onToggleCrewChief={handleToggleCrewChief}
              removing={removingId === assignment.staff_id}
              showCrewChiefToggle={needsCrewChief}
            />
          ))
        )}
      </div>
      
      {/* EF-CREW-CONFLICT-PREVENTION-V1: Conflict Override Dialog */}
      <AlertDialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Scheduling Conflict Detected
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  This crew member has overlapping shift assignments:
                </p>
                
                {/* Conflict Details */}
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {pendingConflicts.map((conflict, idx) => (
                    <div 
                      key={idx} 
                      className="p-2 rounded bg-amber-500/10 border border-amber-500/30 text-sm"
                    >
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        <Clock className="h-3.5 w-3.5 text-amber-600" />
                        {conflict.conflicting_shift_name}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {conflict.conflicting_shift_date} • {conflict.other_effective_start?.split(' ')[1]} - {conflict.other_effective_end?.split(' ')[1]}
                      </div>
                      <div className="text-xs text-amber-600 mt-1">
                        Overlap: {conflict.overlap_minutes} minutes
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Override Reason */}
                <div className="pt-2">
                  <Label className="text-xs font-medium text-muted-foreground">
                    Reason for override (required):
                  </Label>
                  <Textarea
                    value={conflictOverrideReason}
                    onChange={(e) => setConflictOverrideReason(e.target.value)}
                    placeholder="e.g., Crew member confirmed availability, short overlap acceptable..."
                    className="mt-1 h-20 text-sm resize-none"
                    data-testid="conflict-override-reason"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setConflictDialogOpen(false);
                setPendingConflicts([]);
                setConflictOverrideReason("");
                setPendingAssignment(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConflictOverride}
              disabled={!conflictOverrideReason.trim() || assigning}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="conflict-override-confirm"
            >
              {assigning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-2" />
              )}
              Override & Assign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ShiftCrewAssignment;
